/**
 * Store Access Validation Utilities
 *
 * ENG-2884: Centralized utilities for validating store access permissions.
 * Used to prevent associates from accessing stores they don't have authorization for.
 */

/**
 * Logs security events related to store access violations.
 *
 * @param {Object} context - Context about the security event
 * @param {string} context.action - The action that was attempted (e.g., 'view_message', 'send_message')
 * @param {number|string} context.storeId - The unauthorized store ID
 * @param {string} context.messageType - Type of message (SMS, Facebook, Instagram, Google)
 * @param {string} [context.source] - Source of the action (e.g., 'socketRouter', 'sendChatMessage')
 */
export const logUnauthorizedStoreAccess = context => {
  const event = {
    timestamp: new Date().toISOString(),
    severity: 'WARNING',
    category: 'SECURITY',
    event: 'UNAUTHORIZED_STORE_ACCESS',
    ...context,
  };

  console.warn('[SECURITY] Unauthorized store access attempt:', event);

  // Send to LogRocket if available
  if (typeof window !== 'undefined' && window.LogRocket) {
    window.LogRocket.track('security:unauthorized_store_access', event);
  }
};

/**
 * Checks if a user has access to a specific store.
 *
 * @param {number|string} storeId - The store ID to check
 * @param {Array<{id: number}>} stores - Array of store objects the user has access to
 * @returns {boolean} - True if user has access, false otherwise
 *
 * @example
 * const stores = [{ id: 1, name: 'Store 1' }, { id: 2, name: 'Store 2' }];
 * hasStoreAccess(1, stores); // true
 * hasStoreAccess(999, stores); // false
 * hasStoreAccess(null, stores); // false
 * hasStoreAccess(0, stores); // false (0 is not a valid store ID)
 */
export const hasStoreAccess = (storeId, stores) => {
  // Validate storeId - must be a positive number
  // Note: Number(null) returns 0, Number(undefined) returns NaN
  if (storeId === null || storeId === undefined) {
    return false;
  }

  const numericStoreId = Number(storeId);

  // Check for NaN, zero, or negative store IDs
  if (Number.isNaN(numericStoreId) || numericStoreId <= 0) {
    return false;
  }

  // Validate stores array
  if (!stores || !Array.isArray(stores) || stores.length === 0) {
    return false;
  }

  // Check if store ID exists in user's authorized stores
  const storeIds = stores.map(store => store.id);
  return storeIds.includes(numericStoreId);
};

/**
 * Checks if a user has access to a specific store using props object.
 * Convenience wrapper that extracts stores from props.
 *
 * @param {number|string} storeId - The store ID to check
 * @param {Object} props - Props object containing stores array
 * @param {Array<{id: number}>} props.stores - Array of store objects
 * @returns {boolean} - True if user has access, false otherwise
 */
export const hasStoreAccessFromProps = (storeId, props) => {
  const { stores } = props || {};
  return hasStoreAccess(storeId, stores);
};

/**
 * Returns a validated store ID for sending messages.
 * If requestedStoreId is authorized, returns it. Otherwise returns selectedStore.
 *
 * @param {number|string} requestedStoreId - The store ID requested for sending
 * @param {number} selectedStore - The currently selected store ID (fallback)
 * @param {Array<{id: number}>} stores - Array of authorized stores
 * @param {Object} [logContext] - Optional context for logging unauthorized attempts
 * @returns {number|null} - A validated store ID the user has access to, or null if none valid
 *
 * @example
 * const stores = [{ id: 1 }, { id: 2 }];
 * getValidatedStoreId(1, 2, stores); // 1 (requested is valid)
 * getValidatedStoreId(999, 2, stores); // 2 (fallback to selected)
 * getValidatedStoreId(999, 888, stores); // null (neither valid)
 */
export const getValidatedStoreId = (requestedStoreId, selectedStore, stores, logContext = null) => {
  // Check if requested store is valid
  if (requestedStoreId && hasStoreAccess(requestedStoreId, stores)) {
    return Number(requestedStoreId);
  }

  // Log unauthorized attempt if requested store was provided but invalid
  if (requestedStoreId && logContext) {
    logUnauthorizedStoreAccess({
      ...logContext,
      storeId: requestedStoreId,
      action: 'send_message',
      fallbackStoreId: selectedStore,
    });
  }

  // Fall back to selected store if valid
  if (selectedStore && hasStoreAccess(selectedStore, stores)) {
    return Number(selectedStore);
  }

  // No valid store ID found
  return null;
};

/**
 * Returns a validated store ID using props object.
 * Convenience wrapper that extracts values from messaging and auth props.
 *
 * @param {Object} props - Component props
 * @param {Object} props.messaging - Messaging state
 * @param {Object} props.messaging.client - Client object with sendStoreId
 * @param {Object} props.auth - Auth state
 * @param {number} props.auth.selectedStore - Currently selected store
 * @param {Array<{id: number}>} props.auth.stores - User's authorized stores
 * @param {Object} [logContext] - Optional context for logging
 * @returns {number|null} - Validated store ID or null
 */
export const getValidatedStoreIdFromProps = (props, logContext = null) => {
  const { sendStoreId } = props?.messaging?.client || {};
  const { selectedStore, stores } = props?.auth || {};

  return getValidatedStoreId(sendStoreId, selectedStore, stores, logContext);
};
